import * as React from 'react';
import styled from 'styled-components';

import { renderMarkdown } from '../../../utils/Markdown';

interface Props {
  text?: string;
}

export const Body: React.FC<Props> = React.memo((props) => {
  let text = props.text ?? '';
  text = text.replace(/http:/g, 'https:');

  const markDownDomRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        // Remove old spinners on rerender
        (node as HTMLElement).querySelectorAll('img').forEach((img: HTMLImageElement) => {
          if (img.complete && img.naturalWidth > 0) {
            img.style.visibility = 'visible';
            return;
          }
          // Place a loading stub for when images are loading and hide it when they load
          // If an image fails to load then update the message
          const stub = createFailedImageStub(img);
          // eslint-disable-next-line no-unused-expressions
          img.parentElement?.insertBefore(stub, img.nextElementSibling);
          img.onload = () => {
            stub.remove();
            img.style.visibility = 'visible';
          };
          img.onerror = () => {
            (stub.querySelector('.img-error-msg') as HTMLElement).style.display = 'block';
            (stub.querySelector('.img-error-alt') as HTMLElement).style.display = 'block';
            (stub.querySelector('.spinner') as HTMLElement).style.display = 'none';
          };
        });
      }
    },
    [text]
  );

  return (
    <Container
      className="text"
      key={text.length}
      ref={markDownDomRef}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
    ></Container>
  );
});

Body.displayName = 'Body';

const createFailedImageStub = (node: HTMLImageElement): HTMLElement => {
  const alt = node.getAttribute('alt');
  const html = `
    <div class="image-error">
        <span class="img-error-msg">Unable to load image:</span>
        <span class="img-error-alt">${alt}</span>
        <div class="spinner" />
    </div>
    `;
  const element = document.createElement('div');
  element.innerHTML = html;
  return element;
};

const Container = styled.div`
  padding-right: ${({ theme }) => theme.px.small};
  display: block;
  max-height: 400px;
  overflow-x: hidden;
  overflow-y: overlay;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0px;
  text-align: left;

  * {
    margin-bottom: 4px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 600;
    font-size: 12px;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  ul,
  ol {
    margin: 5px 0;
    width: 100%;
  }

  li {
    list-style-position: inside;
    display: list-item;
    list-style-type: disc;
    width: 100%;

    li {
      margin-left: 18px;
    }
  }

  ol {
    list-style-type: decimal;
    li {
      list-style-type: decimal;
    }
  }

  blockquote {
    border-left: 0.25em solid var(--openfin-ui-textDefault);
    padding: 0 8px;
  }

  img {
    max-width: 100%;
    visibility: hidden;
  }

  .image-error {
    font-size: 12px;
    line-height: 16px;
    font-weight: normal;
    color: var(--openfin-ui-textDefault);
    min-height: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 263px;
    height: 85px;
    margin: 4px auto;
    border: 1px solid var(--openfin-ui-background6);
    border-radius: 4px;
    background: var(--openfin-ui-background4);
    text-align: center;

    .img-error-msg {
      font-weight: bold;
      display: none;
    }

    .img-error-alt {
      font-style: italic;
      display: none;
    }

    .spinner {
      border: 5px solid transparent;
      border-top: 5px solid var(--openfin-ui-background6);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 2s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }
  }
`;
